import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Loader2, Printer, Smartphone, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { desktop, desktopPrintAsync } from '@/lib/printTransport'
import { bridgePrintAlignmentCard, bridgePrintTest } from '@/lib/bhooklyAppPrint'
import { buildAlignmentCardHtml } from '@/lib/printAlignmentCard'
import { MM_PER_OFFSET_COL } from '@/lib/printOffset'
import { getPrintOffsetCols, getReceiptPrinter, setPrintOffsetCols } from '@/lib/stationPrinterMap'

/**
 * Print alignment — one card for every shell (F-FE-N156).
 *
 * Started life as the phone-only "Bhookly print agent" card (F-FE-N113): a text
 * alignment card over the Android intent and a "push slips right by N" number
 * that only the ESC/POS path read. It returned null on the desktop, so when
 * KOF's receipts lost their first characters on the Electron app there was
 * nothing on the screen to fix it with.
 *
 * Now:
 *   • Desktop — prints an HTML alignment card (a 1.5 mm ruler) through the
 *     shell's own printer path, to the receipt printer, and reports the verdict.
 *   • Phone / tablet — the agent paragraph, the test slip and the text card, as
 *     before, now on the configured paper width instead of a hardcoded 80.
 *   • Both — the same nudge field, the same stored number, the same unit.
 *
 * The number is machine-local (localStorage) because it says how the roll sits
 * in THIS device's printer. It is applied to every slip this device prints —
 * on the desktop that includes the station printers, not just receipts.
 */

type DesktopState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'ok'; device: string }
  | { kind: 'failed'; detail: string }

export function PrintAlignmentSetting({
  paperWidthMm,
  printableAreaMm,
}: {
  paperWidthMm: number
  printableAreaMm: number
}) {
  const isDesktop = !!desktop()
  const [msg, setMsg] = useState<string | null>(null)
  const [desk, setDesk] = useState<DesktopState>({ kind: 'idle' })
  // Persisted immediately so the alignment card below prints with it.
  const [offset, setOffset] = useState<number>(() => getPrintOffsetCols())
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const onTest = () => {
    const ok = bridgePrintTest()
    setMsg(
      ok
        ? 'Sent to Android default printer — you should see a “Printing…” toast. First time: Bhookly → Always. Per-station routing uses Admin → Stations → printer target (ip:port).'
        : 'Could not open the Android intent — is the Bhookly app installed on THIS phone?',
    )
  }

  const onPhoneCard = () => {
    const ok = bridgePrintAlignmentCard(paperWidthMm, offset)
    setMsg(
      ok
        ? 'Alignment card sent. If the FULL bar is clipped at an edge, the roll is seated off centre — raise the nudge until the slip sits square. If both bars print identically, this printer ignores margin commands and only the nudge will move anything.'
        : 'Could not reach the print agent — is the Bhookly app running on this device?',
    )
  }

  const onDesktopCard = async () => {
    setDesk({ kind: 'sending' })
    const device = getReceiptPrinter()
    try {
      const html = buildAlignmentCardHtml({ paperWidthMm, printableAreaMm, offsetCols: offset })
      const r = await desktopPrintAsync(html, {
        deviceName: device || undefined,
        paperWidthMm,
        printableAreaMm,
      })
      if (!alive.current) return
      if (!r) setDesk({ kind: 'failed', detail: 'The desktop app is not available.' })
      else if (r.ok) setDesk({ kind: 'ok', device: device || 'Default printer' })
      else setDesk({ kind: 'failed', detail: r.error || 'The printer rejected the card.' })
    } catch (e) {
      if (alive.current) setDesk({ kind: 'failed', detail: e instanceof Error ? e.message : String(e) })
    }
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          {isDesktop ? <Printer className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
          {isDesktop ? 'Print alignment' : 'Bhookly print agent (phone KOT)'}
        </label>
      </div>

      {isDesktop ? (
        <>
          <p className="text-xs text-muted-foreground mt-2">
            If the left edge of receipts or kitchen tickets is cut off on this computer&apos;s
            printer, print the alignment card and count the ruler cells that are missing. The
            nudge applies to every slip this computer prints, including station printers.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void onDesktopCard()}
              disabled={desk.kind === 'sending'}
              className="gap-1.5"
            >
              {desk.kind === 'sending' ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Printer className="h-4 w-4" aria-hidden />
              )}
              {desk.kind === 'sending' ? 'Printing…' : 'Print alignment card'}
            </Button>
          </div>
          {desk.kind === 'ok' && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                Sent to {desk.device}. Cell 1 on the left ruler should be complete — if cells are
                cut off, enter how many below and print again.
              </span>
            </p>
          )}
          {desk.kind === 'failed' && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive" role="alert">
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{desk.detail}</span>
            </p>
          )}
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mt-2">
            Install the <strong>Bhookly</strong> Android app on this phone. Set each kitchen
            station&apos;s <strong>printer target</strong> to its LAN address (e.g. Kitchen{' '}
            <code>192.168.18.10:9100</code>, Rooftop <code>192.168.18.20:9100</code>) under Admin →
            Stations. After Fire KOT, tap <strong>Print</strong> — one Intent sends every slip to
            the right printer. This test button uses the agent&apos;s default IP only.
          </p>
          {msg && <p className="text-xs mt-2 text-foreground">{msg}</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            <Button type="button" variant="outline" size="sm" onClick={onTest}>
              Print test slip
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onPhoneCard}>
              Print alignment card
            </Button>
          </div>
        </>
      )}

      <div className="mt-3 flex items-center gap-2">
        <label className="text-xs text-muted-foreground" htmlFor="print-offset-cols">
          Nudge slips right by
        </label>
        <Input
          id="print-offset-cols"
          type="number"
          min={0}
          max={8}
          value={offset}
          onChange={(e) => {
            const n = Math.max(0, Math.min(8, Number(e.target.value) || 0))
            setOffset(n)
            setPrintOffsetCols(n)
          }}
          className="h-8 w-20"
        />
        <span className="text-xs text-muted-foreground">
          steps — one step is {MM_PER_OFFSET_COL} mm (one character on a kitchen slip). Stored on
          this device only.
        </span>
      </div>
    </div>
  )
}
